import type { BusLoadingItemsResponse, BusLoadingItem, GeplandeRitWithDetails, SalesforceApiResponse, ToggleLoadingRequest } from './salesforce/types';

export type VertrekItem = BusLoadingItem & { customerName: string; routeSequence: number };

/** Dezelfde laadlijst als Bus laden, beperkt tot de nog te rijden stops. */
export function ontbrekendeLading(data: BusLoadingItemsResponse, stopIds: ReadonlySet<string>): VertrekItem[] {
  const items = new Map<string, VertrekItem>();
  for (const stop of data.stops) {
    if (!stopIds.has(stop.ritId)) continue;
    for (const item of stop.items) {
      if (!Object.prototype.hasOwnProperty.call(item, 'status')) {
        throw new Error('De laadstatus kon niet worden gecontroleerd. Probeer het opnieuw.');
      }
      if (item.status !== 'In bus' && item.status !== 'Afgeleverd') {
        items.set(item.itemId, { ...item, customerName: stop.customerName, routeSequence: stop.routeSequence });
      }
    }
  }
  return [...items.values()].sort((a, b) => a.routeSequence - b.routeSequence);
}

export interface VertrekApi {
  getRouteStops(routeId: string): Promise<SalesforceApiResponse<GeplandeRitWithDetails[]>>;
  getBusLoadingItems(routeId: string): Promise<SalesforceApiResponse<BusLoadingItemsResponse>>;
  toggleItemsLoaded(request: ToggleLoadingRequest): Promise<{ success: boolean; message?: string }>;
}

export async function controleerLading(api: VertrekApi, routeId: string, stopIds: ReadonlySet<string>): Promise<VertrekItem[]> {
  // Ook de route zelf opnieuw lezen: sinds het openen kan een nieuwe stop
  // zijn toegevoegd. Een cachelijst mag die lading niet buiten de controle houden.
  const stops = await api.getRouteStops(routeId);
  if (!stops.success || !stops.data) throw new Error(stops.message || 'De stops konden niet worden gecontroleerd.');
  stopIds = new Set(stops.data.filter(s => s.Status__c === 'Ingepland' && !s.Overgeslagen__c).map(s => s.Id));
  const response = await api.getBusLoadingItems(routeId);
  if (!response.success || !response.data) throw new Error(response.message || 'De lading kon niet worden gecontroleerd.');
  if (response.data.route.routeId !== routeId) throw new Error('De laadlijst hoort bij een andere route. Probeer het opnieuw.');
  return ontbrekendeLading(response.data, stopIds);
}

/** Alleen expliciet aangevinkte items schrijven; daarna de echte stand teruglezen. */
export async function bevestigVertreklading(api: VertrekApi, routeId: string, stopIds: ReadonlySet<string>, itemIds: string[]): Promise<VertrekItem[]> {
  const actueel = await controleerLading(api, routeId, stopIds);
  const bevestigd = new Set(itemIds);
  const teLaden = actueel.filter(item => bevestigd.has(item.itemId)).map(item => item.itemId);
  if (teLaden.length) {
    const response = await api.toggleItemsLoaded({ itemIds: teLaden, loaded: true });
    if (!response.success) throw new Error(response.message || 'De laadbevestiging kon niet worden opgeslagen.');
  }
  return controleerLading(api, routeId, stopIds);
}
